import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useParams, useLocation } from 'react-router-dom';
import { LogOut, Plus, Home, Pencil, ChevronDown, Check, Settings, Shield, Inbox as InboxIcon, Sliders, KeyRound, Sun, Moon } from 'lucide-react';
import CreateProjectModal from './CreateProjectModal';
import EditProfileModal from './EditProfileModal';
import AccessTokens from './AccessTokens';
import { useOrg } from '../context/OrgContext';
import './Sidebar.css';
import { absoluteMediaUrl } from '../media';
import { BastionMark, BastionAttribution } from './Brand';
import UserAvatar from './UserAvatar';
import { useTheme } from '../ThemeContext';

/**
 * OrgIdentity — vertical stack of org logo (if any) + name/switcher.
 * Renders nothing if there's no current org yet.
 */
function OrgIdentity() {
  const { currentOrg } = useOrg();

  if (!currentOrg) return null;

  const logoUrl = currentOrg.logo ? absoluteMediaUrl(currentOrg.logo) : null;

  return (
    <div className="sidebar-identity">
      {/* Product mark first, then the workspace's own logo if it has one:
          Bastion is the product, the org is the tenant (WR-013 §1). */}
      <div className="sidebar-brand">
        <BastionMark size={22} />
        <span className="sidebar-brand-name">Bastion</span>
      </div>
      {logoUrl && (
        <img
          src={logoUrl}
          alt={currentOrg.name}
          className="sidebar-identity-logo"
        />
      )}
      <OrgSwitcher />
    </div>
  );
}

/**
 * OrgSwitcher — top-of-sidebar workspace label.
 *
 *   • 0 memberships  → render nothing (shouldn't normally happen)
 *   • 1 membership   → plain serif label, no chevron, no click target
 *   • 2+ memberships → button + chevron, opens a dropdown of all orgs
 */
function OrgSwitcher() {
  const { memberships, currentOrg, switchOrg } = useOrg();
  const [open, setOpen] = useState(false);

  if (!currentOrg) return null;

  if (memberships.length <= 1) {
    return (
      <h2 className="sidebar-org-name" title={currentOrg.name}>
        {currentOrg.name}
      </h2>
    );
  }

  return (
    <div className="sidebar-org-switcher">
      <button
        className="sidebar-org-toggle"
        onClick={() => setOpen(o => !o)}
        title={currentOrg.name}
      >
        <span className="sidebar-org-name-text">{currentOrg.name}</span>
        <ChevronDown
          size={14}
          className={`sidebar-org-chevron ${open ? 'open' : ''}`}
        />
      </button>

      {open && (
        <>
          <div className="sidebar-org-backdrop" onClick={() => setOpen(false)} />
          <div className="sidebar-org-menu">
            {memberships.map(m => {
              const isActive = m.organization._id === currentOrg._id;
              return (
                <button
                  key={m.organization._id}
                  className={`sidebar-org-item${isActive ? ' active' : ''}`}
                  onClick={() => {
                    setOpen(false);
                    if (!isActive) switchOrg(m.organization._id);
                  }}
                >
                  <span className="sidebar-org-item-name">{m.organization.name}</span>
                  {isActive && <Check size={14} className="sidebar-org-item-check" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function Sidebar({ user, projects, onLogout, onCreateProject, onUpdateUser }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showTokensModal, setShowTokensModal] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { projectId } = useParams();
  const location = useLocation();
  const [inboxCount, setInboxCount] = useState(0);
  const [isDASEnrolled, setIsDASEnrolled] = useState(false);



  // Poll for inbox count (DAS — slice 1f). A successful response also
  // signals the user is enrolled as a DAS Person; a 403 means they
  // aren't, and we hide the DAS Admin entry.
  useEffect(() => {
    fetchInboxCount();
    const interval = setInterval(fetchInboxCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Enrolment changes when an HDO enrols someone, not minute to minute —
  // probe once rather than on the polling interval.
  useEffect(() => { probeDASEnrolment(); }, []);

  // Refresh inbox count whenever the route changes — so completing an
  // assignment in a DT detail page drops the count immediately when
  // the user navigates back.
  useEffect(() => { fetchInboxCount(); }, [location.pathname]);

  // WR-019: clearing the Inbox happens without a route change and without
  // waiting 30s for the next poll, so the Inbox page broadcasts the counts
  // it just fetched. The event carries the payload, so this costs no extra
  // request; a malformed one falls back to asking the server.
  useEffect(() => {
    const onCounts = (e) => {
      const badge = e.detail?.badge;
      if (typeof badge === 'number') setInboxCount(badge);
      else fetchInboxCount();
    };
    window.addEventListener('bastion:inbox-counts', onCounts);
    return () => window.removeEventListener('bastion:inbox-counts', onCounts);
  }, []);

  // Badge = everything needing attention across every source (WR-004):
  // unread mentions + comment activity + due tasks + open assurance work.
  const fetchInboxCount = async () => {
    try {
      const response = await axios.get('/inbox/counts');
      setInboxCount(response.data?.badge || 0);
    } catch (error) {
      // Quiet failure — a badge is not worth an error state.
      setInboxCount(0);
    }
  };

  // DAS enrolment is a SEPARATE question from the inbox badge, and this is
  // load-bearing: it gates the "DAS Admin" nav entry.
  //
  // /api/inbox is the DAS-only endpoint and 403s when the user has no Person
  // record — exactly the signal needed. The unified /inbox/counts
  // deliberately does NOT 403 (a user without DAS access still has tasks and
  // mentions), so reading enrolment from it would show DAS Admin to everyone.
  const probeDASEnrolment = async () => {
    try {
      await axios.get('/inbox');
      setIsDASEnrolled(true);
    } catch (error) {
      // Not enrolled, or org context not ready.
      setIsDASEnrolled(false);
    }
  };

  return (
    <>
      <div className="sidebar">
        {/* Workspace identity: optional rectangular logo on top, name below */}
        <div className="sidebar-header">
          <OrgIdentity />
        </div>

        {/* User profile */}
        <div className="sidebar-user" onClick={() => setShowProfileModal(true)} title="Edit profile">
          <div className="sidebar-user-avatar-wrap">
            {/* WR-018 §2: through UserAvatar, so a failed load degrades to
                initials instead of painting alt={user.name} in the circle.
                This also settles a pre-existing mismatch — the image state
                was 96px and the initials fallback 36px; both are 96px now. */}
            <UserAvatar user={user} className="sidebar-user-avatar-img" />
          </div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user.name}</div>
            <div className="sidebar-user-email">{user.email}</div>
          </div>
          <Pencil size={14} className="sidebar-user-edit-icon" />
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          <Link to="/" className={`sidebar-nav-item ${!projectId && location.pathname === '/' ? 'active' : ''}`}>
            <Home size={20} />
            <span>All projects</span>
          </Link>

          <Link to="/inbox" className={`sidebar-nav-item ${location.pathname === '/inbox' ? 'active' : ''}`}>
            <InboxIcon size={20} />
            <span>Inbox</span>
            {inboxCount > 0 && (
              <span className="sidebar-nav-badge">{inboxCount > 99 ? '99+' : inboxCount}</span>
            )}
          </Link>

          {isDASEnrolled && (
            <Link to="/das-admin" className={`sidebar-nav-item ${location.pathname.startsWith('/das-admin') ? 'active' : ''}`}>
              <Sliders size={20} />
              <span>DAS Admin</span>
            </Link>
          )}

          <Link to="/org/settings" className={`sidebar-nav-item ${location.pathname.startsWith('/org/settings') ? 'active' : ''}`}>
            <Settings size={20} />
            <span>Settings</span>
          </Link>

          {/* Personal access tokens (WR-012). Per-user, not per-org, so it
              sits here rather than under org settings. */}
          <button className="sidebar-nav-item" onClick={() => setShowTokensModal(true)}>
            <KeyRound size={20} />
            <span>Access tokens</span>
          </button>

          <div className="sidebar-section">
            <div className="sidebar-section-header">
              <span>Projects</span>
              <button className="sidebar-add-btn" onClick={() => setShowCreateModal(true)} title="New project">
                <Plus size={16} />
              </button>
            </div>

            <div className="sidebar-projects">
              {projects.map(project => (
                <Link
                  key={project._id}
                  to={`/project/${project._id}`}
                  className={`sidebar-project-item ${projectId === project._id ? 'active' : ''}`}
                  title={project.type === 'design-workflow' ? `${project.name} · Design Assurance` : project.name}
                >
                  <div className="project-color-indicator" style={{ background: project.color }} />
                  <span className="project-name">{project.name}</span>
                  {project.type === 'design-workflow' && (
                    <Shield size={11} className="project-type-icon" aria-label="Design Assurance project" />
                  )}
                </Link>
              ))}

              {projects.length === 0 && (
                <div className="sidebar-empty">
                  <p>No projects yet</p>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowCreateModal(true)}>
                    Create your first project
                  </button>
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {/* Theme toggle (WR-015 §2). Two themes, so a switch rather than a
              picker; the label names the theme you would switch TO. */}
          <button
            className="btn btn-ghost btn-block"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            <span>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</span>
          </button>
          <button className="btn btn-ghost btn-block" onClick={onLogout}>
            <LogOut size={18} />
            <span>Sign out</span>
          </button>
          <BastionAttribution className="sidebar-attribution" />
        </div>
      </div>

      {showCreateModal && (
        <CreateProjectModal onClose={() => setShowCreateModal(false)} onCreate={onCreateProject} />
      )}
      {showProfileModal && (
        <EditProfileModal user={user} onClose={() => setShowProfileModal(false)} onUpdate={onUpdateUser} />
      )}
      {showTokensModal && (
        <AccessTokens onClose={() => setShowTokensModal(false)} />
      )}
    </>
  );
}

export default Sidebar;
